import { useState } from 'react';

import {
  computeLatestTurnFloorPx,
  nextFloorLatch,
  type FloorLatch,
} from './latestTurnFloor';

interface UseLatestTurnFloorArgs {
  agentRunId: string | null | undefined;
  latestUserMessageId: string | null;
  liveNeedsRoom: boolean;
  cleared: boolean;
  viewportHeight: number | null;
  insetPx: number;
}

interface LatestTurnFloorModel {
  latestTurnFloor: number | null;
  latestTurnNeedsRoom: boolean;
}

/**
 * Stored in state and adjusted during render (React's recommended pattern
 * for "sticky until X") so concurrent/discarded renders cannot leave a
 * half-updated ref. The *next* latch value is what drives this render's
 * floor.
 */
export const useLatestTurnFloor = ({
  agentRunId,
  latestUserMessageId,
  liveNeedsRoom,
  cleared,
  viewportHeight,
  insetPx,
}: UseLatestTurnFloorArgs): LatestTurnFloorModel => {
  const [floorLatch, setFloorLatch] = useState<FloorLatch>(() => ({
    agentRunId,
    userMessageId: null,
  }));

  const next = nextFloorLatch(floorLatch, {
    agentRunId,
    cleared,
    latestUserMessageId,
    liveNeedsRoom,
  });
  if (next !== floorLatch) {
    setFloorLatch(next);
  }

  const latestTurnNeedsRoom =
    liveNeedsRoom ||
    (next.userMessageId != null && next.userMessageId === latestUserMessageId);

  return {
    latestTurnFloor: latestTurnNeedsRoom
      ? computeLatestTurnFloorPx(viewportHeight, insetPx)
      : null,
    latestTurnNeedsRoom,
  };
};
